import { RiCloseLine, RiSearchLine } from '@remixicon/react';
import { useEventCallback } from '@lidojs/design-core';
import { useEditor } from '@lidojs/design-editor';
import axios from 'axios';
import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import Masonry from 'react-responsive-masonry';
import { useAsync } from 'react-use';
import { debounce } from 'lodash';
import IconButton from '../../IconButton';
import { useTranslation } from 'react-i18next';

const GraphicContent: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const qRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dataRef = useRef(false);
  const [images, setImages] = useState<
    {
      id: string;
      thumb: string;
      downloadUrl: string;
    }[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const { actions } = useEditor();

  const loadGraphicList = useEventCallback(async (offset: number) => {
    dataRef.current = true;
    setIsLoading(true);
    const params = {
      limit: '100',
      offset: offset + '',
      q: keyword,
    };
    const response = await axios.get<
      {
        id: string;
        thumb: string;
        downloadUrl: string;
      }[]
    >(`/graphics?${new URLSearchParams(params).toString()}`);
    if (offset) {
      setImages((prevState) => {
        prevState.push(...response.data);
        return prevState;
      });
    } else {
      setImages(response.data);
    }
    setIsLoading(false);
    if (response.data.length > 0) {
      dataRef.current = false;
    }
  });
  useAsync(async () => {
    await loadGraphicList(0);
  }, [loadGraphicList]);

  const handleLoadMore = useEventCallback(async (e: Event) => {
    const node = e.target as HTMLDivElement;
    if (
      node.scrollHeight - node.scrollTop - 80 <= node.clientHeight &&
      !dataRef.current
    ) {
      await loadGraphicList(images.length);
    }
  });

  useEffect(() => {
    scrollRef.current?.addEventListener('scroll', handleLoadMore);
    return () => {
      scrollRef.current?.removeEventListener('scroll', handleLoadMore);
    };
  }, [handleLoadMore]);

  const handleSearch = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    setKeyword(qRef.current?.value || '');
    setTimeout(async () => {
      await loadGraphicList(0);
    });
  };
  const addGraphic = async (item: {
    id: string;
    thumb: string;
    downloadUrl: string;
  }) => {
    const res = await axios.get(
      `/graphics/download?url=${window.encodeURIComponent(item.downloadUrl)}`
    );
    const file = res.data.file;
    const parser = new DOMParser();
    const ele = parser.parseFromString(file, 'text/xml').documentElement;
    const viewBox = ele.getAttribute('viewBox')?.split(' ') || [];
    const width =
      viewBox.length === 4 ? +viewBox[2] : +(ele.getAttribute('width') || 100);
    const height =
      viewBox.length === 4 ? +viewBox[3] : +(ele.getAttribute('height') || 100);

    const svgBlob = new Blob([ele.outerHTML], {
      type: 'image/svg+xml;charset=utf-8',
    });
    const svgUrl = URL.createObjectURL(svgBlob);
    actions.addSvgLayer(svgUrl, { width, height }, ele);
    if (isMobile) {
      onClose();
    }
  };

  return (
    <div
      css={(theme) => ({
        width: '100%',
        height: '100%',
        flexDirection: 'column',
        overflowY: 'auto',
        display: 'flex',
      })}
    >
      <div
        css={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          height: 50,
          borderBottom: `1px solid ${theme.colors.stroke}`,
          padding: '0 20px',
        })}
      >
        <p
          css={(theme) => ({
            lineHeight: '48px',
            fontWeight: 600,
            color: theme.colors.content,
            flexGrow: 1,
          })}
        >
          {t('page_editor.graphic')}
        </p>
        <IconButton onClick={onClose}>
          <RiCloseLine size={20} />
        </IconButton>
      </div>
      <div
        css={(theme) => ({
          flexDirection: 'column',
          overflowY: 'auto',
          display: 'flex',
          flexGrow: 1,
        })}
      >
        <div
          css={(theme) => ({
            borderRadius: 4,
            border: `1px solid ${theme.colors.stroke}`,
            margin: 16,
          })}
        >
          <div
            css={(theme) => ({
              height: 40,
              borderRadius: 4,
              paddingLeft: 12,
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            })}
          >
            <div
              css={(theme) => ({
                fontSize: 24,
                marginRight: 8,
                flexShrink: 0,
              })}
            >
              <RiSearchLine size={20} />
            </div>

            <input
              ref={qRef}
              css={(theme) => ({
                width: '100%',
                height: '100%',
                backgroundColor: theme.colors.input,
                paddingLeft: 8,
              })}
              type={'text'}
              onInput={debounce(handleSearch, 400)}
            />
          </div>
        </div>
        <div
          ref={scrollRef}
          css={(theme) => ({
            flexGrow: 1,
            overflowY: 'auto',
            padding: '16px',
            gridGap: 8,
            position: 'relative',
          })}
        >
          <Masonry columnsCount={3} gutter="16px">
            {images.map((item, idx) => (
              <div
                css={(theme) => ({
                  width: '100%',
                  paddingBottom: '100%',
                  position: 'relative',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '8px',
                  aspectRatio: '1/1',
                  borderRadius: 5,
                  transition: 'box-shadow 0.3s',
                  backgroundColor: theme.colors.backgroundItem,
                  ':hover': {
                    boxShadow: theme.shadow.hoverItem,
                  },
                })}
                key={idx}
                onClick={() => addGraphic(item)}
              >
                <img key={item.id} loading="lazy" src={item.thumb} />
              </div>
            ))}
          </Masonry>
          {isLoading && (
            <div
              css={(theme) => ({
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                fontSize: 16,
              })}
            >
              Loading...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GraphicContent;
